import React, { useState, useEffect, useRef } from 'react';
import { User } from '../types';
import { supabase } from '../services/supabaseService';
import {
  testBullhornConnection,
  fetchBullhornCandidatesRaw,
  fetchBullhornJobOrdersRaw,
  type BullhornAuthData,
} from '../services/bullhornService';
import {
  Database,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  Loader2,
  Settings2,
  Eye,
  EyeOff,
  ArrowRight,
  Users,
  Briefcase,
} from 'lucide-react';
import BullhornInteractiveMapper from './BullhornInteractiveMapper';
import BullhornInteractiveVacancyMapper from './BullhornInteractiveVacancyMapper';
import BullhornFieldMapper from './BullhornFieldMapper';
import BullhornSyncConsole from './BullhornSyncConsole';
import { GitBranch, Upload } from 'lucide-react';
import { loadBullhornSyncState } from '../services/bullhornSyncStore';

interface BullhornIntegrationProps {
  user: User;
  onBack?: () => void;
}

const BullhornIntegration: React.FC<BullhornIntegrationProps> = ({ user, onBack }) => {
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<'success' | 'error' | null>(null);
  const [errorMessage, setErrorMessage] = useState('');

  const [pageTab, setPageTab] = useState<'mapper' | 'import' | 'sync'>('mapper');
  const [isSyncConsoleOpen, setIsSyncConsoleOpen] = useState(false);
  const [syncStateHint, setSyncStateHint] = useState<{ initialFullSyncCompleted?: boolean; lastSyncAt?: string }>({});
  const [isInteractiveMapperOpen, setIsInteractiveMapperOpen] = useState(false);
  const [isInteractiveVacancyMapperOpen, setIsInteractiveVacancyMapperOpen] = useState(false);

  const [bhClientId, setBhClientId] = useState('');
  const [bhClientSecret, setBhClientSecret] = useState('');
  const [bhApiUsername, setBhApiUsername] = useState('');
  const [bhApiPassword, setBhApiPassword] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isSavingCreds, setIsSavingCreds] = useState(false);
  const [credsSaved, setCredsSaved] = useState(false);

  const [bhToken, setBhToken] = useState<string | null>(null);
  const [restUrl, setRestUrl] = useState<string | null>(null);
  const [bullhornSettings, setBullhornSettings] = useState<Record<string, unknown> | null>(null);
  const connectInFlight = useRef(false);

  const [bhCandidateCount, setBhCandidateCount] = useState<number | null>(null);
  const [bhVacancyCount, setBhVacancyCount] = useState<number | null>(null);
  const [bhOpenVacancyCount, setBhOpenVacancyCount] = useState<number | null>(null);
  const [wtCandidateCount, setWtCandidateCount] = useState<number | null>(null);
  const [wtVacancyCount, setWtVacancyCount] = useState<number | null>(null);
  const [wtCandidatesUnlinked, setWtCandidatesUnlinked] = useState<number | null>(null);
  const [wtVacanciesUnlinked, setWtVacanciesUnlinked] = useState<number | null>(null);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [connectedAt, setConnectedAt] = useState<Date | null>(null);

  const loadWerftoolsStats = async () => {
    if (!user.clientId) return;
    const cid = user.clientId as string;
    const [{ count: candTotal }, { count: vacTotal }, { data: candRows }, { data: vacRows }] =
      await Promise.all([
        supabase.from('candidates').select('*', { count: 'exact', head: true }).eq('client_id', cid),
        supabase.from('vacancies').select('*', { count: 'exact', head: true }).eq('client_id', cid),
        supabase.from('candidates').select('ai_parsed_data').eq('client_id', cid),
        supabase.from('vacancies').select('bullhorn_id').eq('client_id', cid),
      ]);
    setWtCandidateCount(candTotal ?? 0);
    setWtVacancyCount(vacTotal ?? 0);
    const unlinkedCand =
      (candRows || []).filter((r) => {
        const parsed = r.ai_parsed_data as { id?: unknown } | null;
        return parsed?.id == null || parsed.id === '';
      }).length;
    const unlinkedVac = (vacRows || []).filter((r) => !String(r.bullhorn_id ?? '').trim()).length;
    setWtCandidatesUnlinked(unlinkedCand);
    setWtVacanciesUnlinked(unlinkedVac);
  };

  const loadBullhornStats = async (auth: BullhornAuthData) => {
    setIsLoadingStats(true);
    setStatsError(null);
    try {
      const [candidates, vacancies, openVacancies] = await Promise.all([
        fetchBullhornCandidatesRaw(auth, 'isDeleted:0', false),
        fetchBullhornJobOrdersRaw(auth, 'isDeleted:0', true, false),
        fetchBullhornJobOrdersRaw(auth, 'isDeleted:0 AND isOpen:1', true, false),
        loadWerftoolsStats(),
      ]);
      setBhCandidateCount(candidates.total ?? candidates.data?.length ?? 0);
      setBhVacancyCount(vacancies.total ?? vacancies.data?.length ?? 0);
      setBhOpenVacancyCount(openVacancies.total ?? openVacancies.data?.length ?? 0);
      setConnectedAt(new Date());
    } catch (err: any) {
      setStatsError(err?.message ?? String(err));
    } finally {
      setIsLoadingStats(false);
    }
  };

  const clearConnectionStats = () => {
    setBhCandidateCount(null);
    setBhVacancyCount(null);
    setBhOpenVacancyCount(null);
    setWtCandidateCount(null);
    setWtVacancyCount(null);
    setWtCandidatesUnlinked(null);
    setWtVacanciesUnlinked(null);
    setConnectedAt(null);
    setStatsError(null);
  };

  useEffect(() => {
    const fetchCreds = async () => {
      try {
        const { data } = await supabase
          .from('clients')
          .select('bullhorn_settings')
          .eq('id', user.clientId)
          .single();

        if (data?.bullhorn_settings) {
          const s = data.bullhorn_settings as Record<string, unknown>;
          setBullhornSettings(s);
          setBhClientId((s.clientId as string) || '');
          setBhClientSecret((s.clientSecret as string) || '');
          setBhApiUsername((s.apiUsername as string) || '');
          setBhApiPassword((s.apiPassword as string) || '');
        }
      } catch (err) {
        console.error('Failed to fetch creds:', err);
      }
    };
    if (user.clientId) fetchCreds();
  }, [user.clientId]);

  useEffect(() => {
    if (!user.clientId) return;
    void loadBullhornSyncState(user.clientId).then(setSyncStateHint);
  }, [user.clientId, isSyncConsoleOpen]);

  const handleSaveCredentials = async () => {
    setIsSavingCreds(true);
    setCredsSaved(false);
    try {
      const { error } = await supabase
        .from('clients')
        .update({
          bullhorn_settings: {
            ...(bullhornSettings || {}),
            clientId: bhClientId,
            clientSecret: bhClientSecret,
            apiUsername: bhApiUsername,
            apiPassword: bhApiPassword,
          },
        })
        .eq('id', user.clientId);

      if (error) throw error;
      setCredsSaved(true);
      setTimeout(() => setCredsSaved(false), 3000);
    } catch (err: any) {
      alert('Failed to save credentials: ' + err.message);
    } finally {
      setIsSavingCreds(false);
    }
  };

  const handleRefreshAuth = async () => {
    try {
      const data = await testBullhornConnection({
        clientId: bhClientId,
        clientSecret: bhClientSecret,
        apiUsername: bhApiUsername,
        apiPassword: bhApiPassword,
      });
      setBhToken(data.BhRestToken);
      setRestUrl(data.restUrl);
      const auth = { BhRestToken: data.BhRestToken, restUrl: data.restUrl };
      await loadBullhornStats(auth);
      return auth;
    } catch (err) {
      console.error('Refresh auth failed', err);
      return null;
    }
  };

  const handleTestConnection = async () => {
    if (connectInFlight.current) return;
    connectInFlight.current = true;
    setIsTesting(true);
    setTestResult(null);
    setErrorMessage('');

    try {
      if (!bhClientId || !bhClientSecret || !bhApiUsername || !bhApiPassword) {
        throw new Error('Missing Bullhorn credentials. Please fill all fields.');
      }

      const authData = await testBullhornConnection({
        clientId: bhClientId,
        clientSecret: bhClientSecret,
        apiUsername: bhApiUsername,
        apiPassword: bhApiPassword,
      });

      const auth = { BhRestToken: authData.BhRestToken, restUrl: authData.restUrl };
      setBhToken(authData.BhRestToken);
      setRestUrl(authData.restUrl);
      setTestResult('success');
      await loadBullhornStats(auth);
    } catch (err: any) {
      setTestResult('error');
      clearConnectionStats();
      if (err.message?.startsWith('{')) {
        try {
          const parsed = JSON.parse(err.message);
          setErrorMessage(parsed.message || parsed.error || 'Connection failed.');
        } catch {
          setErrorMessage(err.message);
        }
      } else {
        setErrorMessage(err.message || String(err) || 'Unknown error occurred.');
      }
    } finally {
      connectInFlight.current = false;
      setIsTesting(false);
    }
  };

  const bhAuth = bhToken && restUrl ? { BhRestToken: bhToken, restUrl } : null;

  return (
    <div className="p-6 lg:p-10 max-w-6xl mx-auto space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-black text-slate-900 tracking-tight leading-tight flex items-center gap-2">
            <Database className="text-[#e51b23] shrink-0" size={24} /> Bullhorn Interactive Mapping
          </h1>
          <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mt-1">
            Map candidates and vacancies from Bullhorn into Werftools
          </p>
        </div>
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="px-4 py-2 bg-slate-100 text-slate-600 rounded-xl text-xs font-bold uppercase tracking-widest hover:bg-slate-200 transition-colors"
          >
            Terug
          </button>
        )}
      </div>

      <div className="bg-white rounded-[2rem] p-8 border border-slate-100 shadow-sm space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-black text-slate-900 uppercase tracking-widest flex items-center gap-2">
            <Settings2 size={16} className="text-slate-500" /> API Credentials
          </h2>
          {credsSaved && (
            <span className="flex items-center gap-1 text-[10px] font-black text-emerald-600 bg-emerald-50 px-2 py-1 rounded border border-emerald-100 uppercase">
              <CheckCircle2 size={12} /> Opgeslagen
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-2">Client ID</label>
            <input
              type="text"
              value={bhClientId}
              onChange={(e) => setBhClientId(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-mono outline-none transition-all placeholder:text-slate-300"
              placeholder="e.g. 98f1682b-..."
            />
          </div>
          <div className="space-y-1">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-2">Client Secret</label>
            <div className="relative">
              <input
                type={showSecret ? 'text' : 'password'}
                value={bhClientSecret}
                onChange={(e) => setBhClientSecret(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 pr-10 text-sm font-mono outline-none transition-all placeholder:text-slate-300"
                placeholder="••••••••••••"
              />
              <button
                type="button"
                onClick={() => setShowSecret(!showSecret)}
                tabIndex={-1}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 focus:outline-none"
              >
                {showSecret ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-2">API Username</label>
            <input
              type="text"
              value={bhApiUsername}
              onChange={(e) => setBhApiUsername(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-mono outline-none transition-all placeholder:text-slate-300"
              placeholder="e.g. werfpl.api"
            />
          </div>
          <div className="space-y-1">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-2">API Password</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={bhApiPassword}
                onChange={(e) => setBhApiPassword(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 pr-10 text-sm font-mono outline-none transition-all placeholder:text-slate-300"
                placeholder="••••••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 focus:outline-none"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 justify-end pt-2">
          <button
            type="button"
            onClick={handleSaveCredentials}
            disabled={isSavingCreds || isTesting}
            className="bg-slate-900 text-white px-6 py-3 rounded-xl font-black text-xs uppercase tracking-widest shadow-lg hover:bg-black transition-all flex items-center justify-center gap-2"
          >
            <span className="inline-flex items-center justify-center gap-2">
              {isSavingCreds ? (
                <Loader2 key="save-spinner" size={16} className="animate-spin shrink-0" aria-hidden />
              ) : null}
              <span>{isSavingCreds ? 'Opslaan...' : 'Opslaan'}</span>
            </span>
          </button>
          <button
            type="button"
            onClick={() => void handleTestConnection()}
            disabled={isTesting || isSavingCreds}
            className="bg-[#e51b23] text-white px-6 py-3 rounded-xl font-black text-xs uppercase tracking-widest shadow-lg hover:bg-[#cc171e] transition-all flex items-center justify-center gap-2"
          >
            <span className="inline-flex items-center justify-center gap-2">
              {isTesting ? (
                <Loader2 key="connect-spinner" size={16} className="animate-spin shrink-0" aria-hidden />
              ) : (
                <RefreshCw key="connect-icon" size={16} className="shrink-0" aria-hidden />
              )}
              <span>{isTesting ? 'Verifiëren...' : 'Verbinden'}</span>
            </span>
          </button>
        </div>

        {testResult === 'error' && (
          <div className="p-4 bg-red-50 text-red-600 text-xs font-bold rounded-xl border border-red-100 flex items-start gap-2">
            <AlertCircle size={16} className="shrink-0 mt-0.5" />
            {errorMessage}
          </div>
        )}
      </div>

      {bhAuth && (
        <div className="bg-white rounded-[2rem] p-8 border border-emerald-100 shadow-sm space-y-5 ring-1 ring-emerald-50">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="relative flex h-3 w-3 shrink-0" aria-hidden>
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
                <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500" />
              </span>
              <div>
                <h2 className="text-sm font-black text-slate-900 uppercase tracking-widest flex items-center gap-2">
                  <CheckCircle2 size={16} className="text-emerald-600" /> Verbonden met Bullhorn
                </h2>
                <p className="text-[11px] text-slate-500 font-medium mt-0.5">
                  {bhApiUsername ? (
                    <span>
                      API-gebruiker <span className="font-mono text-slate-700">{bhApiUsername}</span>
                    </span>
                  ) : null}
                  {connectedAt && (
                    <span>
                      {bhApiUsername ? ' · ' : ''}
                      {connectedAt.toLocaleString('nl-NL')}
                    </span>
                  )}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => void loadBullhornStats(bhAuth)}
              disabled={isLoadingStats || isTesting}
              className="shrink-0 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl border border-slate-200 bg-slate-50 text-slate-700 text-[10px] font-black uppercase tracking-widest hover:bg-white transition-colors disabled:opacity-50"
            >
              <span className="inline-flex items-center gap-2">
                {isLoadingStats ? (
                  <Loader2 size={14} className="animate-spin shrink-0" aria-hidden />
                ) : (
                  <RefreshCw size={14} className="shrink-0" aria-hidden />
                )}
                <span>Tellingen vernieuwen</span>
              </span>
            </button>
          </div>

          {restUrl && (
            <p className="text-[10px] font-mono text-slate-400 truncate" title={restUrl}>
              {restUrl}
            </p>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-slate-50 rounded-2xl border border-slate-100 p-5">
              <Users size={20} className="text-emerald-600 mb-3" />
              <span className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">
                Kandidaten — Werftools vs Bullhorn
              </span>
              <div className="grid grid-cols-2 gap-3 text-center">
                <div className="bg-white rounded-xl border border-violet-100 p-3">
                  <span className="text-[9px] font-black text-violet-600 uppercase tracking-widest">Werftools</span>
                  <span className="block text-2xl font-black text-slate-900 tabular-nums mt-1 min-h-[32px] flex items-center justify-center">
                    {isLoadingStats && wtCandidateCount === null ? (
                      <Loader2 size={22} className="animate-spin text-slate-300" aria-hidden />
                    ) : wtCandidateCount != null ? (
                      wtCandidateCount.toLocaleString('nl-NL')
                    ) : (
                      '—'
                    )}
                  </span>
                  {wtCandidatesUnlinked != null && wtCandidatesUnlinked > 0 && (
                    <span className="text-[9px] font-bold text-violet-600 mt-1 block">
                      {wtCandidatesUnlinked.toLocaleString('nl-NL')} zonder BH-koppeling
                    </span>
                  )}
                </div>
                <div className="bg-white rounded-xl border border-blue-100 p-3">
                  <span className="text-[9px] font-black text-blue-600 uppercase tracking-widest">Bullhorn</span>
                  <span className="block text-2xl font-black text-slate-900 tabular-nums mt-1 min-h-[32px] flex items-center justify-center">
                    {isLoadingStats && bhCandidateCount === null ? (
                      <Loader2 size={22} className="animate-spin text-slate-300" aria-hidden />
                    ) : bhCandidateCount != null ? (
                      bhCandidateCount.toLocaleString('nl-NL')
                    ) : (
                      '—'
                    )}
                  </span>
                </div>
              </div>
            </div>
            <div className="bg-slate-50 rounded-2xl border border-slate-100 p-5">
              <Briefcase size={20} className="text-pink-600 mb-3" />
              <span className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">
                Vacatures — Werftools vs Bullhorn
              </span>
              <div className="grid grid-cols-2 gap-3 text-center">
                <div className="bg-white rounded-xl border border-violet-100 p-3">
                  <span className="text-[9px] font-black text-violet-600 uppercase tracking-widest">Werftools</span>
                  <span className="block text-2xl font-black text-slate-900 tabular-nums mt-1 min-h-[32px] flex items-center justify-center">
                    {isLoadingStats && wtVacancyCount === null ? (
                      <Loader2 size={22} className="animate-spin text-slate-300" aria-hidden />
                    ) : wtVacancyCount != null ? (
                      wtVacancyCount.toLocaleString('nl-NL')
                    ) : (
                      '—'
                    )}
                  </span>
                  {wtVacanciesUnlinked != null && wtVacanciesUnlinked > 0 && (
                    <span className="text-[9px] font-bold text-violet-600 mt-1 block">
                      {wtVacanciesUnlinked.toLocaleString('nl-NL')} zonder BH-koppeling
                    </span>
                  )}
                </div>
                <div className="bg-white rounded-xl border border-blue-100 p-3">
                  <span className="text-[9px] font-black text-blue-600 uppercase tracking-widest">Bullhorn</span>
                  <span className="block text-2xl font-black text-slate-900 tabular-nums mt-1 min-h-[32px] flex items-center justify-center">
                    {isLoadingStats && bhVacancyCount === null ? (
                      <Loader2 size={22} className="animate-spin text-slate-300" aria-hidden />
                    ) : bhVacancyCount != null ? (
                      bhVacancyCount.toLocaleString('nl-NL')
                    ) : (
                      '—'
                    )}
                  </span>
                  {bhOpenVacancyCount != null && (
                    <span className="text-[9px] font-bold text-blue-600 mt-1 block">
                      {bhOpenVacancyCount.toLocaleString('nl-NL')} open
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {statsError && (
            <p className="text-xs text-amber-700 font-medium bg-amber-50 border border-amber-100 rounded-xl px-4 py-3">
              Kon tellingen niet ophalen: {statsError}
            </p>
          )}
        </div>
      )}

      <div className="flex gap-2 p-1 bg-slate-100 rounded-xl w-fit">
        <button
          type="button"
          onClick={() => setPageTab('mapper')}
          className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest flex items-center gap-2 transition-all ${
            pageTab === 'mapper' ? 'bg-white text-indigo-700 shadow-sm' : 'text-slate-500 hover:text-slate-800'
          }`}
        >
          <GitBranch size={14} /> Veldmapper
        </button>
        <button
          type="button"
          onClick={() => setPageTab('import')}
          className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest flex items-center gap-2 transition-all ${
            pageTab === 'import' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800'
          }`}
        >
          <Upload size={14} /> Data-import
        </button>
        <button
          type="button"
          onClick={() => setPageTab('sync')}
          className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest flex items-center gap-2 transition-all ${
            pageTab === 'sync' ? 'bg-white text-[#e51b23] shadow-sm' : 'text-slate-500 hover:text-slate-800'
          }`}
        >
          <RefreshCw size={14} /> Synchronisatie
        </button>
      </div>

      {pageTab === 'mapper' && user.clientId && (
        <BullhornFieldMapper clientId={user.clientId as string} bhAuth={bhAuth} />
      )}

      {pageTab === 'sync' && (
        <div className={`bg-white rounded-[2rem] p-8 border border-slate-100 shadow-sm space-y-5 ${!bhAuth ? 'opacity-50 pointer-events-none' : ''}`}>
          <div>
            <h2 className="text-sm font-black text-slate-900 uppercase tracking-widest">Bullhorn data synchronisatie</h2>
            <p className="text-xs text-slate-500 font-medium mt-2 leading-relaxed max-w-2xl">
              Eén keer alle data uit Bullhorn importeren (CRM, kandidaten, vacatures, plaatsingen, pipelines en
              pipeline-geschiedenis). Daarna alleen gewijzigde records. In de wijzigingentabel zie je per regel of iets{' '}
              <span className="text-blue-700 font-bold">nieuw in Bullhorn</span>, een{' '}
              <span className="text-amber-700 font-bold">wijziging in beide systemen</span>, of{' '}
              <span className="text-violet-700 font-bold">alleen in Werftools</span> staat.
            </p>
            {syncStateHint.initialFullSyncCompleted && syncStateHint.lastSyncAt && (
              <p className="text-[11px] text-emerald-700 font-bold mt-2">
                Laatste sync: {new Date(syncStateHint.lastSyncAt).toLocaleString('nl-NL')}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={() => setIsSyncConsoleOpen(true)}
            disabled={!bhAuth}
            className="inline-flex items-center justify-center w-full sm:w-auto px-8 py-4 bg-[#e51b23] hover:bg-[#cc171e] text-white text-xs font-black uppercase tracking-widest rounded-xl shadow-lg gap-2 disabled:cursor-not-allowed"
          >
            <RefreshCw size={18} />
            Open sync-console
          </button>
          {!bhAuth && (
            <p className="text-xs text-slate-500">Verbind eerst met Bullhorn via de credentials hierboven.</p>
          )}
        </div>
      )}

      {pageTab === 'import' && (
        <div className={`space-y-4 transition-opacity ${!bhAuth ? 'opacity-50 pointer-events-none' : ''}`}>
          <button
            type="button"
            onClick={() => setIsInteractiveMapperOpen(true)}
            disabled={!bhAuth}
            className="inline-flex items-center justify-center w-full px-6 py-4 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white text-[15px] font-black tracking-tight rounded-xl shadow-md hover:shadow-lg transition-all gap-3 disabled:cursor-not-allowed"
          >
            <Database size={20} className="text-emerald-100" />
            Kandidaat Interactive Mapper
            <ArrowRight size={18} className="ml-auto opacity-50" />
          </button>

          <button
            type="button"
            onClick={() => setIsInteractiveVacancyMapperOpen(true)}
            disabled={!bhAuth}
            className="inline-flex items-center justify-center w-full px-6 py-4 bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white text-[15px] font-black tracking-tight rounded-xl shadow-md hover:shadow-lg transition-all gap-3 disabled:cursor-not-allowed"
          >
            <Database size={20} className="text-pink-100" />
            Vacature Interactive Mapper
            <ArrowRight size={18} className="ml-auto opacity-50" />
          </button>

          {!bhAuth && (
            <p className="text-xs text-slate-500 font-medium text-center">
              Sla credentials op en klik op Verbinden om de import-mappers te openen.
            </p>
          )}
        </div>
      )}

      {isInteractiveMapperOpen && bhAuth && (
        <BullhornInteractiveMapper
          user={user}
          bhAuth={bhAuth}
          onRefreshAuth={handleRefreshAuth}
          onClose={() => setIsInteractiveMapperOpen(false)}
        />
      )}

      {isInteractiveVacancyMapperOpen && bhAuth && (
        <BullhornInteractiveVacancyMapper
          user={user}
          bhAuth={bhAuth}
          onClose={() => setIsInteractiveVacancyMapperOpen(false)}
        />
      )}

      {isSyncConsoleOpen && (
        <BullhornSyncConsole
          user={user}
          bhAuth={bhAuth}
          credentials={{
            clientId: bhClientId,
            clientSecret: bhClientSecret,
            apiUsername: bhApiUsername,
            apiPassword: bhApiPassword,
          }}
          onClose={() => {
            setIsSyncConsoleOpen(false);
            if (user.clientId) void loadBullhornSyncState(user.clientId).then(setSyncStateHint);
          }}
        />
      )}
    </div>
  );
};

export default BullhornIntegration;
